/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import Pass from "App/render/webgl/passes/pass";
import WebGLRenderGraph, {ERenderFiler} from "App/render/webgl/render_graph";
import View from "App/view/view";
import {vec3} from "gl-matrix";
import WebGLContext from "App/web_gl_context";
import Factory from "App/render/webgl/passes/factory";

import DepthShader from "App/render/webgl/shaders/depth/depth";
import AlphaDepth from "App/render/webgl/shaders/depth/alpha_depth";
import {
    AlphaTransparencyBinTypeName, BillboardBinTypeName,
    ChunkLODBinTypeName,
    OpaqueBinTypeName,
    OpaqueNoTextureBinTypeName
} from "App/render/webgl/nodes/bins/type_names";
import {RenderOptionNodeTypeName} from "App/render/webgl/nodes/type_names";
import {DepthLowResPassTypeName} from "App/render/webgl/passes/type_names";
import IScene from "js_interface/src/scene_interface";

export default
class DepthLowRes extends Pass {

    public static TypeName: string = DepthLowResPassTypeName;

    constructor(aSceneAPI: IScene, aRenderGraph: WebGLRenderGraph) {
        super(aSceneAPI, aRenderGraph);
    }


    public static create(aSceneAPI: IScene, aRenderGraph: WebGLRenderGraph): DepthLowRes {
        return new DepthLowRes(aSceneAPI, aRenderGraph);
    }

    public initialize() {
        let shaders = this._renderGraph.getShaders();

        this._shadersToBins = {};

        this.attachShaderToBin(OpaqueBinTypeName, shaders.get(DepthShader.TypeName));
        this.attachShaderToBin(OpaqueNoTextureBinTypeName, shaders.get(DepthShader.TypeName));
        this.attachShaderToBin(ChunkLODBinTypeName, shaders.get(DepthShader.TypeName));
        this.attachShaderToBin(AlphaTransparencyBinTypeName, shaders.get(AlphaDepth.TypeName));
        this.attachShaderToBin(BillboardBinTypeName, shaders.get(DepthShader.TypeName));
    };

    public readPoints(aView, aProperties): vec3[] {
        let framebuffer = this._renderGraph.getFramebuffers().lowResDepth;

        framebuffer.bind();

        let points = [];
        for (let i = 0; i < aProperties.readPosition.length; i += 2) {
            points.push(
                framebuffer.readPoint(
                    aView,
                    aProperties.readPosition[i + 0],
                    aProperties.readPosition[i + 1])
            );
        }

        framebuffer.unbind();

        return points;
    }

    public render(aView: View, aProperties: any) {
        let framebuffer = this._renderGraph.getFramebuffers().lowResDepth;

        let scale = framebuffer.parameters.scale;
        let gl = (<WebGLContext>this._context).getWebGLRenderingContext();

        // set rendering target
        framebuffer.bind();
        let position = aView.getPosition();
        let size = aView.getSize();

        // set viewport
        gl.enable(gl.SCISSOR_TEST);
        gl.viewport(position[0] * scale, position[1] * scale, size[0] * scale, size[1] * scale);
        gl.scissor(position[0] * scale, position[1] * scale, size[0] * scale, size[1] * scale);

        gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);

        // render all the bins related to the depth bc pass
        let bins = this._renderGraph.getRenderBins();

        this.view = aView;

        this._somethingRendered = false;

        // we reuse the buffer so we decide the type of render here
        switch (aProperties.type) {
            case "cameraAboveTerrain":
                this.state.setRenderNodesFilters([{
                    type: RenderOptionNodeTypeName,
                    value: ERenderFiler.keepCameraAbove
                }]);

                this._somethingRendered = this._somethingRendered || bins.get(OpaqueBinTypeName).render(this);
                this._somethingRendered = this._somethingRendered || bins.get(OpaqueNoTextureBinTypeName).render(this);
                this._somethingRendered = this._somethingRendered || bins.get(ChunkLODBinTypeName).render(this);
                this._somethingRendered = this._somethingRendered || bins.get(AlphaTransparencyBinTypeName).render(this);
                this._somethingRendered = this._somethingRendered || bins.get(BillboardBinTypeName).render(this);

                this.state.resetRenderNodesFilters();
                break;
            case "cameraCollision":
                this._somethingRendered = this._somethingRendered || bins.get(OpaqueBinTypeName).render(this);
                this._somethingRendered = this._somethingRendered || bins.get(OpaqueNoTextureBinTypeName).render(this);
                this._somethingRendered = this._somethingRendered || bins.get(ChunkLODBinTypeName).render(this);
                this._somethingRendered = this._somethingRendered || bins.get(AlphaTransparencyBinTypeName).render(this);
                break;
        }

        gl.disable(gl.SCISSOR_TEST);

        framebuffer.unbind(this._typeName);

        this.view = null;

        return this._somethingRendered;
    }
}

Factory.register(DepthLowRes);
